import React, { ReactNode, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import { ModalDialog, ModalDialogTitle, NavigationSection } from 'uiComponents/popups/modal'
import { Body, Caption, Link } from 'uiComponents/typography'
import ActionButton from 'uiComponents/buttons'
import { crmSelectors } from 'crm/selectors'
import { useHasFeature } from 'features'
import { Audience } from 'crm/schema'
import { Configuration } from './componentsService'
import { Trans, useTranslation } from 'translations'

interface SubmitOptions {
    submit: () => void
    saveDraft: (publish?: boolean) => void
}

export type HandleStudioSubmit = (values: Configuration, options: SubmitOptions) => void

const formatAudienceName = (audiences?: (Audience | undefined)[]) => {
    if (!Array.isArray(audiences)) {
        return
    }

    return audiences
        .filter((audience) => !!audience)
        .map((audience: Audience) => audience.name)
        .join(', ')
}

interface AudienceConfirmationProps {
    children({ handleSubmit }: { handleSubmit: HandleStudioSubmit }): ReactNode
}

const AudienceConfirmation: React.FC<AudienceConfirmationProps> = ({ children }) => {
    const hasFeature = useHasFeature('CRMAudiencesPage')
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const selectedAudiencesRef = useRef<string[]>()
    const audiences = useSelector(crmSelectors.findAudienceBySlugArray(selectedAudiencesRef.current))
    const optionsRef = useRef<SubmitOptions>()
    const closeModal = () => setOpen(false)
    const handleSubmit: HandleStudioSubmit = (values, options) => {
        if (hasFeature) {
            const selectedAudiences = get(values, 'productConfig.audiences')
            const marketingConsent = get(values, 'productConfig.marketingConsent')
            selectedAudiencesRef.current = selectedAudiences
            const isAudienceFilterApplied = !!selectedAudiences?.length && !marketingConsent

            if (isAudienceFilterApplied) {
                selectedAudiencesRef.current = selectedAudiences
                optionsRef.current = options
                return setOpen(true)
            }
        }

        return options.submit()
    }

    return (
        <>
            {open && (
                <ModalDialog
                    onDismiss={closeModal}
                    interactive
                    fromTop="20%"
                    style={{
                        width: '508px',
                    }}
                >
                    <ModalDialogTitle>{t('engageTools.studio.audienceConfirmation.title')}</ModalDialogTitle>
                    <Body size={1} style={{ paddingBottom: '2em' }}>
                        <Trans
                            i18nKey="engageTools.studio.audienceConfirmation.selectedAudiences"
                            values={{ audiences: formatAudienceName(audiences) }}
                            components={{ b: <b /> }}
                        />
                    </Body>
                    <Body size={1} style={{ paddingBottom: '2em' }}>
                        <Trans i18nKey="engageTools.studio.audienceConfirmation.consentWarning" components={{ b: <b /> }} />
                    </Body>
                    <Caption>
                        <Trans
                            i18nKey="engageTools.studio.audienceConfirmation.moreInfo"
                            components={{
                                anchor: (
                                    <Link
                                        href="https://support.convious.com/help/audiences-in-your-engage-tools-overview-campaign-inspiration"
                                        target="_blank"
                                    />
                                ),
                            }}
                        />
                    </Caption>
                    <NavigationSection>
                        <ActionButton secondary size="large" onClick={closeModal}>
                            {t('common.cancel')}
                        </ActionButton>
                        <ActionButton
                            secondary
                            kind="action"
                            size="large"
                            onClick={() => {
                                closeModal()
                                optionsRef.current?.saveDraft(false)
                            }}
                        >
                            {t('engageTools.studio.audienceConfirmation.saveAsDraft')}
                        </ActionButton>

                        <ActionButton
                            size="large"
                            onClick={() => {
                                closeModal()
                                optionsRef.current?.submit()
                            }}
                        >
                            {t('engageTools.studio.audienceConfirmation.publish')}
                        </ActionButton>
                    </NavigationSection>
                </ModalDialog>
            )}

            {children({ handleSubmit })}
        </>
    )
}

export default AudienceConfirmation
